import { useAtomValue } from "jotai";
import classNames from "classnames";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { DEFAULT_NODE_COLOR } from "@zapier/canvas-constants";
import {
  DefaultNodeData,
  isPublicAtom,
  NodeType,
  togglesAtomResettable,
} from "@zapier/canvas-state";

import { getItemsForStepSummary } from "../../../../utils/getItemsForStepSummary";
import { determineAssetType } from "../../../../utils/determineAssetType";
import { isAssetNode } from "../../../../utils/isAssetNode";
import { AssetHeading } from "../../../AssetHeading";
import { StepTitle } from "../../../StepTitle";
import { StepSummary } from "../../../StepSummary";
import { TimeSpent } from "../../../TimeMetrics";
import { StepUsage } from "../../../StepUsage";
import { StepStatusRow } from "../../../StepStatusRow";
import { StepDetails } from "../../../StepDetails";
import { FormsSubmissionsCount } from "../../../FormsSubmissionsCount";
import { FlexRow } from "../../../FlexRow";
import styles from "./DefaultNode.module.css";

interface DefaultNodeContentProps {
  nodeType: NodeType;
  data: DefaultNodeData;
  id: string;
  isSelected?: boolean;
  isInEditMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

export function DefaultNodeContent({
  data,
  id,
  isSelected = false,
  className,
  style,
  children,
  nodeType,
}: DefaultNodeContentProps) {
  const { label, services, color = DEFAULT_NODE_COLOR, actors } = data;
  const isAssetNodeType = isAssetNode(data);
  const toggles = useAtomValue(togglesAtomResettable);

  const isPublic = useAtomValue(isPublicAtom);

  const hasNote = !isRemirrorJSONEmpty(data.notesJson);

  const dynamicStyles = {
    "--border-color": color,
    ...style,
  };

  const stepSummaryItems = getItemsForStepSummary({
    apps: services,
    actors,
    hasNote: Boolean(hasNote),
  });
  // Currently only supporting details for table nodes and interface nodes
  const expandable =
    (!isPublic &&
      (Boolean(data.tableId) ||
        Boolean(data.projectId) ||
        Boolean(data.pageId))) ||
    Boolean(data.proposalId);

  const canNodeGetAiSuggestion =
    toggles.showSmartNodeSuggest &&
    !data.app &&
    !data.projectId &&
    !data.pageId &&
    !data.chatbotId;

  return (
    <div
      id={isAssetNodeType ? "asset-node" : "default-node"}
      className={classNames(
        styles.wrapper,
        {
          [styles.assetNode]: isAssetNodeType,
        },
        className,
      )}
      style={dynamicStyles as React.CSSProperties}
    >
      {children}
      <StepStatusRow
        nodeId={id}
        assetType={determineAssetType(data.services, nodeType)}
        zapId={null}
        interfaceProjectId={data.projectId}
        interfacePageId={data.pageId}
        tableId={data.tableId}
        chatbotId={data.chatbotId}
      />
      <StepTitle
        nodeId={id}
        label={label}
        isSelected={isSelected}
        canNodeGetAiSuggestion={canNodeGetAiSuggestion}
      />
      {!isPublic && (
        <StepUsage tableId={data.tableId} chatbotId={data.chatbotId} />
      )}
      <FlexRow>
        <FormsSubmissionsCount
          nodeId={id}
          interfaceProjectId={data.projectId}
          interfacePageId={data.pageId}
        />
        <TimeSpent timeSpent={data.timeSpent} />
      </FlexRow>
      <AssetHeading
        nodeId={id}
        assetType={determineAssetType(services, nodeType)}
        tableId={data.tableId}
        interfaceProjectId={data.projectId}
        interfacePageId={data.pageId}
        chatbotId={data.chatbotId}
      />
      {stepSummaryItems.length > 0 && (
        <StepSummary
          items={stepSummaryItems}
          isAssetNodeType={isAssetNodeType}
        />
      )}
      {expandable && (
        <StepDetails
          nodeId={id}
          proposalId={data.proposalId}
          tableId={data.tableId}
          interfacePageId={data.pageId}
          interfaceProjectId={data.projectId}
        />
      )}
    </div>
  );
}
